import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Plus, ExternalLink, Eye, EyeOff, Archive, Globe, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export default function Videos() {
  const { user } = useAuth();
  const canAdd = user?.role === "director" || user?.role === "teacher";
  const [videos, setVideos] = useState([]);
  const [classes, setClasses] = useState([]);
  const empty = { title: "", url: "", visibility: "all", class_ids: [], routine_name: "", practice_notes: "", section: "General", visible_to_dancers: true, visible_to_parents: true };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [classQ, setClassQ] = useState("");

  const load = () => {
    api.get("/videos").then((r) => setVideos(r.data));
    api.get("/classes").then((r) => setClasses(r.data));
  };
  useEffect(load, []);
  const classMap = Object.fromEntries(classes.map((c) => [c.id, c]));
  const classNameOf = (id) => classMap[id]?.name || "class";

  const save = async () => {
    if (!/^https?:\/\//.test(form.url)) { toast.error("Link must start with http:// or https://"); return; }
    if (form.visibility === "specific" && form.class_ids.length === 0) { toast.error("Pick at least one class, or choose 'All classes'"); return; }
    try {
      if (editId) await api.put(`/videos/${editId}`, form); else await api.post("/videos", form);
      toast.success("Video saved"); setOpen(false); setForm(empty); setEditId(null); setClassQ(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const archive = async (id) => { await api.post(`/videos/${id}/archive`); toast.success("Archived"); load(); };

  const openEdit = (v) => { setForm({ ...empty, ...v, visibility: v.visibility || "specific", class_ids: v.class_ids || (v.class_id ? [v.class_id] : []) }); setEditId(v.id); setClassQ(""); setOpen(true); };
  const toggleClass = (cid) => setForm((f) => ({ ...f, class_ids: f.class_ids.includes(cid) ? f.class_ids.filter((x) => x !== cid) : [...f.class_ids, cid] }));
  const classMatches = classes.filter((c) => c.active !== false && !form.class_ids.includes(c.id) && (!classQ || (c.name || "").toLowerCase().includes(classQ.toLowerCase()))).slice(0, 8);

  const VisibilityLabel = ({ v }) => (
    v.visibility === "all"
      ? <Badge className="bg-teal-light text-teal-dark rounded-full text-[10px] flex items-center gap-1"><Globe className="w-3 h-3" />All classes</Badge>
      : <Badge variant="outline" className="rounded-full text-[10px] flex items-center gap-1"><Users className="w-3 h-3" />{(v.class_ids || []).length ? (v.class_ids || []).map(classNameOf).join(", ") : "No classes"}</Badge>
  );

  return (
    <div className="stagger-in">
      <PageHeader title="Practice Videos" subtitle="Rehearse anywhere, anytime." testid="videos-page"
        action={canAdd && <Button data-testid="add-video-button" onClick={() => { setForm(empty); setEditId(null); setClassQ(""); setOpen(true); }} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Add Video</Button>} />

      {videos.length === 0 ? <EmptyState icon={Video} title="No practice videos yet!" hint="Get ready to dance." testid="videos-empty" /> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videos.map((v) => (
            <div key={v.id} data-testid={`video-card-${v.id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col">
              <div className="aspect-video bg-black">
                {v.embed_url ? (
                  <iframe title={v.title} src={v.embed_url} className="w-full h-full" allowFullScreen frameBorder="0" />
                ) : (
                  <a href={v.url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center text-white gap-2">
                    <ExternalLink className="w-8 h-8 text-teal" /><span className="text-sm font-semibold">Open Practice Video</span>
                  </a>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant="outline" className="rounded-full text-[10px]">{v.platform}</Badge>
                  <Badge className="bg-teal-light text-teal-dark rounded-full text-[10px]">{v.section}</Badge>
                </div>
                <p className="font-head font-bold">{v.title}</p>
                {v.routine_name && <p className="text-xs text-muted-foreground mt-0.5">{v.routine_name}</p>}
                {v.practice_notes && <p className="text-sm mt-2 text-muted-foreground">{v.practice_notes}</p>}
                {canAdd && (
                  <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid={`video-visibility-${v.id}`}>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Dancers see:</span>
                    <VisibilityLabel v={v} />
                  </div>
                )}
                {canAdd && (
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" className="rounded-full flex-1" data-testid={`edit-video-${v.id}`} onClick={() => openEdit(v)}>Edit</Button>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`archive-video-${v.id}`} onClick={() => archive(v.id)}><Archive className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Edit" : "Add"} Practice Video</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="video-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Video link (YouTube, Vimeo, Drive…)</Label><Input data-testid="video-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" /></div>

            <div className="space-y-1.5">
              <Label>Who can see this (dancers)</Label>
              <div className="flex gap-2">
                {[["all", "All classes"], ["specific", "Specific classes"]].map(([val, lbl]) => (
                  <button key={val} type="button" data-testid={`video-visibility-${val}`} onClick={() => setForm({ ...form, visibility: val })}
                    className={`flex-1 rounded-full h-10 text-sm font-semibold border ${form.visibility === val ? "bg-teal text-white border-teal" : "bg-white"}`}>{lbl}</button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{form.visibility === "all" ? "Every dancer in the studio can practice with this video." : "Only dancers enrolled in at least one selected class will see it."}</p>
            </div>

            {form.visibility === "specific" && (
              <div className="space-y-1.5">
                <Label>Classes</Label>
                <div className="flex flex-wrap gap-1.5 mb-1">
                  {form.class_ids.length === 0 ? <span className="text-xs text-muted-foreground">No classes yet — search below.</span> :
                    form.class_ids.map((cid) => <span key={cid} data-testid={`video-class-chip-${cid}`} className="inline-flex items-center gap-1 rounded-full bg-teal text-white text-xs px-2.5 py-1">{classNameOf(cid)}<button type="button" onClick={() => toggleClass(cid)} className="ml-0.5">×</button></span>)}
                </div>
                <Input data-testid="video-class-search" value={classQ} onChange={(e) => setClassQ(e.target.value)} placeholder="Search classes to add…" />
                {classQ && (
                  <div className="mt-1 border rounded-lg divide-y max-h-40 overflow-y-auto">
                    {classMatches.length === 0 ? <p className="text-xs text-muted-foreground p-2">No matching classes.</p> :
                      classMatches.map((c) => <button key={c.id} type="button" data-testid={`video-class-option-${c.id}`} onClick={() => { toggleClass(c.id); setClassQ(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">{c.name}</button>)}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Routine name</Label><Input value={form.routine_name} onChange={(e) => setForm({ ...form, routine_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Section</Label><Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Practice notes</Label><Textarea value={form.practice_notes} onChange={(e) => setForm({ ...form, practice_notes: e.target.value })} /></div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.visible_to_dancers} onCheckedChange={(v) => setForm({ ...form, visible_to_dancers: v })} /><Eye className="w-4 h-4" />Dancers</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.visible_to_parents} onCheckedChange={(v) => setForm({ ...form, visible_to_parents: v })} /><EyeOff className="w-4 h-4" />Parents</label>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-video-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save Video</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
